import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Trash2, Download, ShieldAlert } from "lucide-react";
import DeveloperApi from "../components/DeveloperApi";
import { FundingSourceSelector } from "@/components/FundingSourceSelector";
import AccountClosure from "@/components/AccountClosure";
import ComplianceDisclosures from "@/components/ComplianceDisclosures";
import { setCompanyTz } from "@/lib/tz";
import { useCompanyTz } from "@/context/CompanyTzContext";
import AppleIdentityCard from "@/components/AppleIdentityCard";
import { LaunchReadiness } from "@/components/LaunchReadiness";
import SovereignStatus from "@/components/SovereignStatus";
import { useAuth } from "@/context/AuthContext";

const SCHEDULES = [
  { v: "weekly", l: "Weekly (52/yr)" },
  { v: "biweekly", l: "Biweekly (26/yr)" },
  { v: "semimonthly", l: "Semi-monthly (24/yr)" },
  { v: "monthly", l: "Monthly (12/yr)" },
];

const WEEK_START_DAYS = [
  { v: "monday", l: "Monday" },
  { v: "sunday", l: "Sunday" },
];

// IANA zones offered in the picker. Storage stays UTC; this is what a date, a
// shift reading and a pay-period boundary are resolved against.
const TIMEZONES = [
  { v: "UTC", l: "UTC (no daylight saving)" },
  { v: "America/New_York", l: "Eastern — New York" },
  { v: "America/Chicago", l: "Central — Chicago" },
  { v: "America/Denver", l: "Mountain — Denver" },
  { v: "America/Phoenix", l: "Mountain — Phoenix (no DST)" },
  { v: "America/Los_Angeles", l: "Pacific — Los Angeles" },
  { v: "America/Anchorage", l: "Alaska — Anchorage" },
  { v: "Pacific/Honolulu", l: "Hawaii — Honolulu (no DST)" },
  { v: "America/Puerto_Rico", l: "Atlantic — Puerto Rico (no DST)" },
];

const DEFAULT_PRIMARY = "#0A0A0A";
const DEFAULT_ACCENT = "#00A050";
export default function Company() {
  const { user } = useAuth();
  const { refresh: refreshTz } = useCompanyTz();
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  const [proActive, setProActive] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    // StrictMode mounts twice; a late second response must not clobber edits
    // the user has already made to the form.
    let cancelled = false;
    (async () => {
      const { data } = await api.get("/company");
      if (cancelled) return;
      setC(data || {});
      setCompanyTz(data?.timezone);
      try {
        const { data: bs } = await api.get("/billing/status");
        if (!cancelled) setProActive(!!bs?.pro?.active);
      } catch { if (!cancelled) setProActive(false); }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!c) return <div className="p-10 font-mono">Loading…</div>;
  const upd = (k) => (e) => setC({ ...c, [k]: e.target.value });

  const onLogo = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 500 * 1024) { toast.error("Logo must be under 500KB"); return; }
    const reader = new FileReader();
    reader.onload = () => setC({ ...c, logo_data_url: String(reader.result) });
    reader.readAsDataURL(f);
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        name: c.name || "", ein: c.ein || "", address: c.address || "",
        city: c.city || "", state: c.state || "", zip_code: c.zip_code || "",
        bank_name: c.bank_name || "", bank_routing: c.bank_routing || "", bank_account: c.bank_account || "",
        pay_schedule: c.pay_schedule || "biweekly",
        overtime_threshold: Math.min(40, Math.max(1, Number(c.overtime_threshold ?? 40) || 40)),
        first_pay_date: c.first_pay_date || null,
        work_week_start: c.work_week_start || "monday",
        timezone: c.timezone || "UTC",
        initial_period_start: c.initial_period_start || null,
        initial_period_end: c.initial_period_end || null,
        initial_pay_date: c.initial_pay_date || null,
        email_alerts_enabled: c.email_alerts_enabled !== false,
        portal_visibility_hides_unreleased_stubs: c.portal_visibility_hides_unreleased_stubs !== false,
        logo_data_url: c.logo_data_url || "",
        primary_color: c.primary_color || DEFAULT_PRIMARY,
        accent_color: c.accent_color || DEFAULT_ACCENT,
        predictor_enabled: c.predictor_enabled !== false,
        payroll_budget_weekly: Number(c.payroll_budget_weekly || 0),
        remote_tracking_enabled: !!c.remote_tracking_enabled,
      };
      await api.put("/company", payload);
      // Apply a timezone change immediately — every timestamp screen reads it.
      setCompanyTz(payload.timezone);
      refreshTz();
      toast.success("Company updated — refresh to see branding across the app");
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <div className="p-10">
      <div className="overline mb-2">§ COMPANY</div>
      <h1 className="font-heading font-black text-5xl tracking-tight mb-8">Company Settings</h1>
      <div className="mb-10 max-w-5xl space-y-6">
        <SovereignStatus user={user} />
        <FundingSourceSelector />
      </div>
      <div className="grid grid-cols-2 gap-6 max-w-4xl">
        <Section title="COMPANY IDENTITY">
          <F label="Name"><input data-testid="co-name" value={c.name || ""} onChange={upd("name")} className="input" /></F>
          <F label="EIN"><input data-testid="co-ein" value={c.ein || ""} onChange={upd("ein")} className="input" /></F>
          <F label="Address"><input value={c.address || ""} onChange={upd("address")} className="input" /></F>
          <div className="grid grid-cols-3 gap-3">
            <F label="City"><input value={c.city || ""} onChange={upd("city")} className="input" /></F>
            <F label="State"><input value={c.state || ""} onChange={upd("state")} className="input" /></F>
            <F label="ZIP"><input value={c.zip_code || ""} onChange={upd("zip_code")} className="input" /></F>
          </div>
        </Section>
        <Section title="PAYROLL BANK ACCOUNT">
          <F label="Bank Name"><input data-testid="co-bank-name" value={c.bank_name || ""} onChange={upd("bank_name")} className="input" /></F>
          <F label="Routing Number"><input data-testid="co-routing" value={c.bank_routing || ""} onChange={upd("bank_routing")} className="input" /></F>
          <F label="Account Number"><input data-testid="co-account" value={c.bank_account || ""} onChange={upd("bank_account")} className="input" /></F>
          <F label="Pay Schedule">
            <select data-testid="co-schedule" value={c.pay_schedule || "biweekly"} onChange={upd("pay_schedule")} className="input">
              {SCHEDULES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
            </select>
          </F>
          <F label="Weekly Overtime Threshold (hours)">
            <input
              data-testid="co-overtime-threshold"
              type="number" step="0.25" min="1" max="40"
              value={c.overtime_threshold ?? 40}
              onChange={upd("overtime_threshold")}
              className="input"
            />
            <div className="font-mono text-[10px] text-muted-foreground mt-1">
              Hours per work week before 1.5× overtime applies. FLSA §7 / NY §142-2.2 cap this at 40 —
              a lower figure (e.g. a 35h union contract) is honoured everywhere: payroll runs, timesheet
              aggregation and client billing.
            </div>
          </F>
          <F label="First Pay Date (anchors the payroll calendar)">
            <input
              data-testid="co-first-pay-date"
              type="date"
              value={c.first_pay_date || ""}
              onChange={upd("first_pay_date")}
              className="input"
            />
            <div className="font-mono text-[10px] text-muted-foreground mt-1">
              Weekly / biweekly cycles anchor to this date. Leave blank for auto-detect.
            </div>
          </F>
          <F label="Work Week Start Day">
            <select
              data-testid="co-work-week-start"
              value={c.work_week_start || "monday"}
              onChange={upd("work_week_start")}
              className="input"
            >
              {WEEK_START_DAYS.map((d) => <option key={d.v} value={d.v}>{d.l}</option>)}
            </select>
            <div className="font-mono text-[10px] text-muted-foreground mt-1">
              Timesheet rows start on this day for every employee.
            </div>
          </F>
          <F label="Time Zone">
            <select
              data-testid="co-timezone"
              value={c.timezone || "UTC"}
              onChange={upd("timezone")}
              className="input"
            >
              {TIMEZONES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
            </select>
            <div className="font-mono text-[10px] text-muted-foreground mt-1">
              Every timestamp is stored in UTC. This is the clock your crew
              works to — it decides what "9:00 AM", a pay-period boundary and
              a work week mean, and it keeps hours correct across daylight
              saving changes.
            </div>
          </F>
        </Section>
        <Section title="NOTIFICATION & VISIBILITY SUITE">
          <div className="font-mono text-[10px] text-muted-foreground -mt-2 mb-2">Global toggles that gate what employees see in the portal and what emails leave the app. Both default ON for new companies.</div>
          <label className="flex items-start gap-3 cursor-pointer" data-testid="co-email-alerts-row">
            <input
              data-testid="co-email-alerts-enabled"
              type="checkbox"
              checked={c.email_alerts_enabled !== false}
              onChange={(e) => setC({ ...c, email_alerts_enabled: e.target.checked })}
              className="mt-1 w-4 h-4 accent-ink"
            />
            <div>
              <div className="font-mono text-sm font-black">Email Alerts</div>
              <div className="font-mono text-[10px] text-muted-foreground leading-relaxed">Master kill-switch. When OFF, no pay-stub notifications, release emails, or "resend email" actions will fire — useful during migration or bulk backfill.</div>
            </div>
          </label>
          <label className="flex items-start gap-3 cursor-pointer" data-testid="co-portal-vis-row">
            <input
              data-testid="co-portal-visibility"
              type="checkbox"
              checked={c.portal_visibility_hides_unreleased_stubs !== false}
              onChange={(e) => setC({ ...c, portal_visibility_hides_unreleased_stubs: e.target.checked })}
              className="mt-1 w-4 h-4 accent-ink"
            />
            <div>
              <div className="font-mono text-sm font-black">Portal Visibility · Hide Unreleased Stubs</div>
              <div className="font-mono text-[10px] text-muted-foreground leading-relaxed">When ON, employees only see stubs the admin has explicitly released. Admins always see everything. Recommended ON — turn OFF only if you release stubs outside Checkmate.</div>
            </div>
          </label>
          <label className={"flex items-start gap-3 border-2 border-ink p-3 " + (proActive ? "cursor-pointer bg-warn/10" : "opacity-40 cursor-not-allowed bg-secondary")} data-testid="co-remote-tracking-row">
            <input
              data-testid="co-remote-tracking"
              type="checkbox"
              disabled={!proActive}
              checked={!!c.remote_tracking_enabled}
              onChange={(e) => setC({ ...c, remote_tracking_enabled: e.target.checked })}
              className="mt-1 w-4 h-4 accent-ink"
            />
            <div>
              <div className="font-mono text-sm font-black inline-flex items-center gap-2">
                Remote Tracking · Master Switch
                {!proActive && <span className="px-1.5 py-0.5 border border-ink bg-ink text-white text-[9px] font-mono uppercase tracking-widest">Pro-only</span>}
              </div>
              <div className="font-mono text-[10px] text-muted-foreground leading-relaxed">
                OFF by default. When ON, employees assigned to a <b>Remote / Online</b> worksite will be auto clock-in on portal login, ping a 15-min heartbeat for proof of work, and every login gets an IP geolocation check.
                Employees must accept the on-screen disclosure before any monitoring begins.
                {!proActive && <span className="block mt-1 text-bad"><b>Upgrade to Professional</b> to unlock Remote Work Tracking.</span>}
              </div>
            </div>
          </label>
        </Section>
        <Section title="ONBOARDING SEED (OPTIONAL FIRST PERIOD)">
          <div className="font-mono text-[10px] text-muted-foreground -mt-2 mb-3">
            Use this when your first pay period doesn't fit the standard length (e.g. a 10-day catch-up period during onboarding). All three fields must be set together — the calendar will honor them for period #1 only, then revert to the standard {c.pay_schedule === "weekly" ? "7-day weekly" : "14-day biweekly"} cadence.
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <F label="Initial Period Start">
              <input
                data-testid="co-initial-period-start"
                type="date"
                value={c.initial_period_start || ""}
                onChange={upd("initial_period_start")}
                className="input"
              />
            </F>
            <F label="Initial Period End">
              <input
                data-testid="co-initial-period-end"
                type="date"
                value={c.initial_period_end || ""}
                onChange={upd("initial_period_end")}
                className="input"
              />
            </F>
            <F label="Initial Pay Date">
              <input
                data-testid="co-initial-pay-date"
                type="date"
                value={c.initial_pay_date || ""}
                onChange={upd("initial_pay_date")}
                className="input"
              />
            </F>
          </div>
        </Section>
        <Section title="BRANDING (PROFESSIONAL)">
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 border-2 border-ink bg-white flex items-center justify-center overflow-hidden">
              {c.logo_data_url
                ? <img data-testid="co-logo-preview" src={c.logo_data_url} alt="Company logo" className="max-w-full max-h-full object-contain" />
                : <div className="font-heading font-black text-3xl" data-testid="co-logo-placeholder">{(c.name || "C").trim().charAt(0).toUpperCase()}</div>}
            </div>
            <div className="flex flex-col gap-2">
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/svg+xml" onChange={onLogo} data-testid="co-logo-file" className="hidden" />
              <button type="button" onClick={() => fileRef.current?.click()} data-testid="co-logo-upload" className="border-2 border-ink px-3 py-2 font-mono uppercase text-xs tracking-widest inline-flex items-center gap-2 hover:bg-secondary">
                <Upload className="w-3.5 h-3.5" /> Upload Logo
              </button>
              {c.logo_data_url && (
                <button type="button" onClick={() => setC({ ...c, logo_data_url: "" })} data-testid="co-logo-remove" className="border-2 border-bad text-bad px-3 py-2 font-mono uppercase text-xs tracking-widest inline-flex items-center gap-2 hover:bg-bad hover:text-white">
                  <Trash2 className="w-3.5 h-3.5" /> Remove
                </button>
              )}
              <div className="font-mono text-[10px] text-muted-foreground">PNG / JPG / SVG · under 500KB</div>
            </div>
          </div>
          <F label="Primary Color (nav + buttons)">
            <div className="flex gap-2 items-center">
              <input type="color" data-testid="co-primary-color" value={c.primary_color || DEFAULT_PRIMARY} onChange={upd("primary_color")} className="h-10 w-16 border-2 border-ink" />
              <input value={c.primary_color || DEFAULT_PRIMARY} onChange={upd("primary_color")} className="input" />
            </div>
          </F>
          <F label="Accent Color (highlights + success states)">
            <div className="flex gap-2 items-center">
              <input type="color" data-testid="co-accent-color" value={c.accent_color || DEFAULT_ACCENT} onChange={upd("accent_color")} className="h-10 w-16 border-2 border-ink" />
              <input value={c.accent_color || DEFAULT_ACCENT} onChange={upd("accent_color")} className="input" />
            </div>
          </F>
        </Section>
        <Section title="PAYROLL PREDICTOR (PROFESSIONAL)">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              data-testid="co-predictor-enabled"
              type="checkbox"
              checked={c.predictor_enabled !== false}
              onChange={(e) => setC({ ...c, predictor_enabled: e.target.checked })}
              className="w-4 h-4 accent-ink"
            />
            <span className="font-mono text-sm">Enable live predictions &amp; budget alerts on the Dashboard</span>
          </label>
          <F label="Weekly Payroll Budget ($ — 0 disables budget alerts)">
            <input
              data-testid="co-payroll-budget"
              type="number"
              min="0"
              step="100"
              value={c.payroll_budget_weekly ?? 0}
              onChange={upd("payroll_budget_weekly")}
              className="input"
            />
          </F>
          <div className="font-mono text-[10px] text-muted-foreground">Predictor uses live clock-in data to project this week's total cost. If projected cost exceeds the budget above, admins see a red budget alert on the dashboard.</div>
        </Section>
      </div>
      <button data-testid="save-company" disabled={busy} onClick={save} className="mt-8 hard-btn px-6 py-3 font-mono uppercase text-xs tracking-widest">
        {busy ? "Saving…" : "Save Company Settings"}
      </button>

      {/* Developer API tab — Enterprise white-label licensing */}
      <DeveloperApi />

      {/* Enterprise SSO enforcement toggle */}
      <div className="hard-card p-6 mb-8" data-testid="sso-enforce-section">
        <div className="overline mb-2 flex items-center gap-2">§ ENTERPRISE SSO
          <span className="ml-1 px-1.5 py-0.5 bg-warn text-ink text-[9px] font-black uppercase tracking-widest">Enterprise-Grade</span>
        </div>
        <p className="font-mono text-xs text-muted-foreground max-w-2xl mb-3">Force every admin + employee login to happen through your corporate SSO provider (Google Workspace or Microsoft Entra). Password-based sign-in is blocked while this is on — the only path in becomes the SSO buttons on the login page.</p>
        <label className="flex items-center gap-3 cursor-pointer">
          <input type="checkbox" data-testid="enforce-sso-toggle" checked={!!c?.enforce_corporate_login} onChange={(e) => setC({ ...(c || {}), enforce_corporate_login: e.target.checked })} className="w-5 h-5 accent-ink" />
          <div>
            <div className="font-mono text-sm font-black">Enforce Corporate Login (Google + Microsoft only)</div>
            <div className="font-mono text-[10px] text-muted-foreground">Existing password sessions stay valid; new sign-ins must use SSO. Wire Azure via Company Settings → SSO once your Client ID + Secret land.</div>
          </div>
        </label>
      </div>

      {/* Directorial Sign-off — live launch readiness */}
      <LaunchReadiness />

      {/* Sovereign ID — which sign-in methods reach this account */}
      <AppleIdentityCard />

      {/* Compliance & disclaimers — non-affiliation + calculation sources */}
      <ComplianceDisclosures />

      {/* Right to be Forgotten — GDPR Art. 17 / CCPA §1798.105 */}
      <div className="hard-card p-6 mt-8 border-warn bg-warn/5" data-testid="rtbf-section">
        <div className="overline mb-2 flex items-center gap-2"><ShieldAlert className="w-4 h-4" /> § RIGHT TO BE FORGOTTEN</div>
        <p className="font-mono text-xs text-muted-foreground mb-4 max-w-2xl">
          Under GDPR / CCPA you may export or delete every piece of personal data Checkmate stores about you.
          Downloading returns a signed JSON dump. Deleting removes your login + profile; historical payroll
          records are anonymised (legally-required retention) so they no longer reference you personally.
        </p>
        <div className="flex flex-wrap gap-3">
          <button
            data-testid="rtbf-download"
            onClick={async () => {
              try {
                const res = await api.get("/me/download-data", { responseType: "blob" });
                const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
                const a = document.createElement("a"); a.href = url; a.download = "checkmate-my-data.json"; a.click();
                setTimeout(() => window.URL.revokeObjectURL(url), 1000);
                toast.success("Data export downloaded");
              } catch (e) { toast.error(formatApiError(e)); }
            }}
            className="border-2 border-ink px-4 py-2 font-mono uppercase text-xs tracking-widest hover:bg-secondary inline-flex items-center gap-2"
          ><Download className="w-4 h-4" /> Download My Data</button>
          <button
            data-testid="rtbf-delete"
            onClick={async () => {
              if (!window.confirm("Permanently delete your employee login + profile? Historical payroll records are anonymised. This cannot be undone.")) return;
              try {
                await api.delete("/me/account");
                toast.success("Login deleted — signing out");
                setTimeout(() => { window.location.href = "/login"; }, 1500);
              } catch (e) { toast.error(formatApiError(e)); }
            }}
            className="border-2 border-bad text-bad px-4 py-2 font-mono uppercase text-xs tracking-widest hover:bg-bad hover:text-white inline-flex items-center gap-2"
          ><Trash2 className="w-4 h-4" /> Delete My Login + Profile</button>
        </div>
        <div className="font-mono text-[10px] text-muted-foreground mt-3">
          Closing the whole company account is below, under DELETE MY ACCOUNT.
        </div>
      </div>

      {/* In-app account deletion — App Store Guideline 5.1.1(v) */}
      <AccountClosure />

      <style>{`.input{width:100%;padding:0.65rem 0.75rem;border:2px solid #0A0A0A;font-family:"IBM Plex Mono",monospace;font-size:0.85rem;background:#fff}`}</style>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="hard-card p-6 space-y-4">
      <div className="overline">{title}</div>
      {children}
    </div>
  );
}

function F({ label, children }) {
  return <label className="block"><div className="overline mb-2">{label}</div>{children}</label>;
}
